/**
 * ExportToDevizDialog — selectare template lucrare (hidroizolații etc.)
 * și generare Deviz din măsurătoare.
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, FileText, ChevronRight, Layers, BookOpen, Settings2 } from "lucide-react";
import CustomTemplatesManager from "./CustomTemplatesManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExportToDevizDialog({ companyId, open, onOpenChange, measurement, onDone }) {
  const [templates, setTemplates] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  // selected: { kind: 'predef'|'custom', id: string }
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showManager, setShowManager] = useState(false);

  const params = useMemo(() => ({ company_id: companyId }), [companyId]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/sketches/work-templates`, { params });
      setTemplates(data.templates || []);
      setCustomTemplates(data.custom || []);
      if (!selected) {
        const first = (data.templates || [])[0];
        if (first) setSelected({ kind: "predef", id: first.id });
      }
    } catch (e) {
      console.error(e);
      toast.error("Nu am putut încărca template-urile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadTemplates();
    // eslint-disable-next-line
  }, [open]);

  const activeTemplate = useMemo(() => {
    if (!selected) return null;
    if (selected.kind === "custom") return customTemplates.find((t) => t.id === selected.id) || null;
    return templates.find((t) => t.id === selected.id) || null;
  }, [selected, templates, customTemplates]);
  const area = measurement?.total_area_m2 || 0;
  const perim = measurement?.total_perimeter_m || 0;

  const previewItems = useMemo(() => {
    if (!activeTemplate) return [];
    return activeTemplate.items.map((it) => {
      const base = it.use === "perimeter" ? perim : area;
      const qty = Math.round(base * (it.qty_factor || 1) * 100) / 100;
      const totalMat = qty * (it.materialPrice || 0);
      const totalMan = qty * (it.manoperaPrice || 0);
      return { ...it, qty, totalMat, totalMan };
    });
  }, [activeTemplate, area, perim]);

  const totalDirect = previewItems.reduce((s, it) => s + it.totalMat + it.totalMan, 0);

  const handleExport = async () => {
    if (!measurement?.id || !selected) return;
    try {
      setExporting(true);
      const body = selected.kind === "custom"
        ? { custom_template_id: selected.id }
        : { template_id: selected.id };
      const { data } = await axios.post(
        `${API}/sketches/measurements/${measurement.id}/export-to-deviz`,
        body,
        { params },
      );
      toast.success(`Deviz ${data.numar_deviz} generat cu succes!`);
      onOpenChange(false);
      onDone && onDone(data);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Nu am putut genera devizul");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-teal-600" />
            Exportă în DevizePRO
          </DialogTitle>
          <DialogDescription>
            Alege tipul de lucrare. Cantitățile se calculează automat din arie și perimetru.
          </DialogDescription>
        </DialogHeader>

        {measurement && (
          <div className="rounded-lg bg-teal-50 border border-teal-200 p-3 text-sm text-slate-700">
            <div className="font-semibold text-teal-800 mb-1 flex items-center gap-1.5">
              <Layers className="w-4 h-4" /> {measurement.name}
            </div>
            <div className="text-xs">
              Arie totală: <b>{area.toFixed(2)} m²</b> · Perimetru: <b>{perim.toFixed(2)} m</b>
              {measurement.address && <> · {measurement.address}</>}
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-8 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Se încarcă template-urile…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold px-1">Predefinite</div>
              {templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelected({ kind: "predef", id: t.id })}
                  data-testid={`export-tpl-${t.id}`}
                  className={`w-full text-left rounded-lg border p-3 transition ${
                    selected?.kind === "predef" && selected.id === t.id
                      ? "border-teal-500 bg-teal-50 shadow-sm"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm text-slate-800">{t.name}</div>
                    {selected?.kind === "predef" && selected.id === t.id && <ChevronRight className="w-4 h-4 text-teal-600" />}
                  </div>
                  <Badge variant="outline" className="mt-1 text-[10px]">{t.category}</Badge>
                  <p className="text-xs text-slate-600 mt-1.5 leading-snug">{t.description}</p>
                </button>
              ))}

              <div className="flex items-center justify-between px-1 pt-2">
                <div className="text-[10px] uppercase tracking-wide text-violet-600 font-semibold flex items-center gap-1">
                  <BookOpen className="w-3 h-3" /> Personale
                </div>
                <button
                  type="button"
                  onClick={() => setShowManager(true)}
                  className="text-[10px] text-violet-700 hover:underline flex items-center gap-0.5"
                  data-testid="export-open-manager-btn"
                >
                  <Settings2 className="w-3 h-3" /> Gestionează
                </button>
              </div>
              {customTemplates.length === 0 && (
                <div className="text-xs text-slate-500 italic px-2 py-2 border border-dashed border-slate-200 rounded">
                  Nu ai șabloane personale. Apasă "Gestionează" pentru a crea primul.
                </div>
              )}
              {customTemplates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelected({ kind: "custom", id: t.id })}
                  data-testid={`export-custom-tpl-${t.id}`}
                  className={`w-full text-left rounded-lg border p-3 transition ${
                    selected?.kind === "custom" && selected.id === t.id
                      ? "border-violet-500 bg-violet-50 shadow-sm"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm text-slate-800">{t.name}</div>
                    {selected?.kind === "custom" && selected.id === t.id && <ChevronRight className="w-4 h-4 text-violet-600" />}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    <Badge className="bg-violet-100 text-violet-700 border-0 text-[10px]">{t.category}</Badge>
                    <Badge variant="outline" className="text-[10px]">{t.items?.length || 0} art.</Badge>
                  </div>
                  {t.description && <p className="text-xs text-slate-600 mt-1.5 leading-snug">{t.description}</p>}
                </button>
              ))}
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-sm font-semibold text-slate-700">
                Preview articole ({previewItems.length})
              </div>
              <div className="max-h-[360px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 text-slate-600 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-semibold">Denumire</th>
                      <th className="text-center px-2 py-1.5 font-semibold w-14">UM</th>
                      <th className="text-right px-2 py-1.5 font-semibold w-16">Cant.</th>
                      <th className="text-right px-2 py-1.5 font-semibold w-20">Mat. (RON)</th>
                      <th className="text-right px-2 py-1.5 font-semibold w-20">Manop. (RON)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewItems.map((it) => (
                      <tr key={it.code} className="border-t border-slate-100">
                        <td className="px-2 py-1.5">
                          <div className="text-slate-800">{it.name}</div>
                          <div className="text-[10px] text-slate-400">
                            {it.materialPrice} + {it.manoperaPrice} RON/{it.um}
                            {it.qty_factor !== 1 ? ` · fact. ${it.qty_factor}` : ""}
                          </div>
                        </td>
                        <td className="text-center px-2 py-1.5">{it.um}</td>
                        <td className="text-right px-2 py-1.5 font-semibold">{it.qty.toFixed(2)}</td>
                        <td className="text-right px-2 py-1.5">{it.totalMat.toFixed(2)}</td>
                        <td className="text-right px-2 py-1.5">{it.totalMan.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50">
                    <tr>
                      <td colSpan={4} className="px-2 py-1.5 text-right font-semibold">Total cheltuieli directe:</td>
                      <td className="px-2 py-1.5 text-right font-bold text-teal-700">{totalDirect.toFixed(2)} RON</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="px-3 py-2 bg-amber-50 border-t border-amber-200 text-[11px] text-amber-800">
                * Prețurile sunt orientative. Poți edita valorile în DevizePRO după generare (indirecte, profit, TVA).
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={exporting}>Anulează</Button>
          <Button
            onClick={handleExport}
            disabled={exporting || !selected || (area <= 0 && perim <= 0)}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="export-confirm-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
            Generează Deviz
          </Button>
        </div>

        <CustomTemplatesManager
          companyId={companyId}
          open={showManager}
          onOpenChange={setShowManager}
          onChanged={loadTemplates}
        />
      </DialogContent>
    </Dialog>
  );
}
